import { useRawSignaturePositionStore } from "@/app/stores/raw-signature-position.store.ts";
import { PdfAnnotationBorderStyle, PdfAnnotationSubtype, type PdfSquareAnnoObject } from "@embedpdf/models";
import { type AnnotationScope } from "@embedpdf/plugin-annotation/vue";
import type { useActiveDocument } from "@embedpdf/plugin-document-manager/vue";
import { type ScrollScope } from "@embedpdf/plugin-scroll";
import { isNil } from "lodash";
import { type Ref } from "vue";
import { AbstractSignObject, type SignCoordinates, type UpdateSignCoordinates } from "./abstract.sign-object.domain.ts";

export class SignPosition extends AbstractSignObject {
  static readonly ANNOTATION_TYPE = PdfAnnotationSubtype.SQUARE;

  public get idStr(): string {
    return `${this.id!}`;
  }

  public readonly name = "prépositionnement";

  id: number | undefined = undefined;

  private rawSignPosStore: ReturnType<typeof useRawSignaturePositionStore>;
  private pendingSignId: string | null = null;

  constructor(
    annotationApi: () => Ref<AnnotationScope | null>,
    scrollProvide: () => Ref<ScrollScope | null>,
    activeDocument: () => ReturnType<typeof useActiveDocument>,
    initialCoordinates?: SignCoordinates,
    id?: number
  ) {
    super(annotationApi, scrollProvide, activeDocument, initialCoordinates);
    this.rawSignPosStore = useRawSignaturePositionStore();
    this.id = id;
  }

  async init() {
    if (!this.id) {
      const { id } = await this.rawSignPosStore.create(this.coordinates);
      this.id = id;
    }

    const annot = this.buildAnnotation();
    this.annotationApi().value?.createAnnotation(this.coordinates.pageIndex, annot);
  }

  async delete() {
    await Promise.all([super.delete(), this.rawSignPosStore.delete(this.id!)]);
  }

  linkTo(id: string) {
    this.pendingSignId = id;
  }

  isLinkedTo(id: string) {
    return this.pendingSignId === id;
  }

  unlink() {
    this.pendingSignId = null;
  }

  get isLinked() {
    return !isNil(this.pendingSignId);
  }

  protected async onUpdate(updatedCoordinates: UpdateSignCoordinates) {
    await this.rawSignPosStore.update(this.id!, updatedCoordinates);
  }

  private buildAnnotation(): PdfSquareAnnoObject {
    return {
      ...this.getBaseAnnotation(),
      type: SignPosition.ANNOTATION_TYPE,
      flags: [],
      color: "#FCEEAC",
      opacity: 0.65,
      strokeWidth: 2,
      strokeColor: "#000000",
      strokeStyle: PdfAnnotationBorderStyle.UNKNOWN,
    };
  }
}
